import React from 'react';
import { Alert, Box, Button, Checkbox, Collapse, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import { configureMedicationRound } from '../../services/medicationReminderService';

const weekdayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const buildScheduledMembers = (medications, scheduleByMedicationId) => medications.flatMap((medication) => {
  const schedule = scheduleByMedicationId.get(medication.id);
  if (!schedule?.active || schedule.paused || schedule.kind === 'prn') return [];
  return (schedule.times || []).map((scheduledTime) => ({ medicationId: medication.id, medicationName: medication.name || 'Medication', scheduledTime }));
});

const buildSuggestedRounds = (members) => [...members].sort((a, b) => a.scheduledTime.localeCompare(b.scheduledTime)).reduce((groups, member) => {
  const minutes = Number(member.scheduledTime.slice(0, 2)) * 60 + Number(member.scheduledTime.slice(3, 5));
  const current = groups[groups.length - 1];
  if (!current || minutes - current.startMinutes > 30) groups.push({ startMinutes: minutes, members: [member] });
  else current.members.push(member);
  return groups;
}, []).filter((group) => group.members.length > 1);

const defaultNameForTime = (time) => {
  const hour = Number(time.slice(0, 2));
  if (hour < 12) return 'Morning medications';
  if (hour < 17) return 'Afternoon medications';
  return 'Evening medications';
};

const formatClockTime = (time = '') => {
  const [hourText, minute = '00'] = time.split(':');
  const hour = Number(hourText);
  if (!Number.isFinite(hour)) return time;
  return `${hour % 12 || 12}:${minute}`;
};

const clockPeriod = (time = '') => Number(time.slice(0, 2)) >= 12 ? 'PM' : 'AM';

const MedicationRoundSetup = ({ childId, medications, scheduleByMedicationId, rounds, canManage, focusedMedicationId = '' }) => {
  const members = React.useMemo(() => buildScheduledMembers(medications, scheduleByMedicationId), [medications, scheduleByMedicationId]);
  const suggestions = React.useMemo(() => buildSuggestedRounds(members), [members]);
  const [open, setOpen] = React.useState(false);
  const [editingRoundId, setEditingRoundId] = React.useState('');
  const [draft, setDraft] = React.useState({ name: '', reminderTime: '08:00', memberKeys: [], weekdays: weekdayKeys, startDate: '', endDate: '', active: true });
  const [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const memberKey = (item) => `${item.medicationId}|${item.scheduledTime}`;
  const handledFocusRef = React.useRef('');

  React.useEffect(() => {
    if (!focusedMedicationId || !canManage || handledFocusRef.current === focusedMedicationId) return;
    const round = rounds.find((item) => (item.members || []).some((member) => member.medicationId === focusedMedicationId));
    if (!round) return;
    handledFocusRef.current = focusedMedicationId;
    setEditingRoundId(round.id);
    setDraft({ ...round, memberKeys: (round.members || []).map(memberKey), weekdays: round.weekdays?.length ? round.weekdays : weekdayKeys });
    setOpen(true);
  }, [canManage, focusedMedicationId, rounds]);

  React.useEffect(() => {
    if (!focusedMedicationId || !open || !editingRoundId) return;
    const timer = window.setTimeout(() => {
      document.getElementById(`medication-round-${editingRoundId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [editingRoundId, focusedMedicationId, open]);

  const save = async (round, roundId = '') => {
    setBusy(true); setMessage('');
    try { await configureMedicationRound(childId, round, roundId); setMessage('Medication round saved.'); setOpen(false); setEditingRoundId(''); }
    catch (error) { setMessage(error?.message || 'Medication round could not be saved.'); }
    finally { setBusy(false); }
  };

  const saveSuggestions = async () => {
    setBusy(true); setMessage('');
    try {
      await Promise.all(suggestions.map((suggestion) => configureMedicationRound(childId, {
        name: defaultNameForTime(suggestion.members[0].scheduledTime),
        reminderTime: suggestion.members[0].scheduledTime,
        members: suggestion.members.map(({ medicationId, scheduledTime }) => ({ medicationId, scheduledTime })),
        weekdays: weekdayKeys, active: true,
      })));
      setMessage('Suggested medication rounds are ready.');
    } catch (error) { setMessage(error?.message || 'Suggested rounds could not be saved.'); }
    finally { setBusy(false); }
  };

  const selectedMembers = members.filter((item) => draft.memberKeys.includes(memberKey(item)));
  const selectedMinutes = selectedMembers.map((item) => Number(item.scheduledTime.slice(0, 2)) * 60 + Number(item.scheduledTime.slice(3, 5)));
  const selectionOutsideRoundWindow = selectedMinutes.length > 1 && Math.max(...selectedMinutes) - Math.min(...selectedMinutes) > 30;
  const availableMemberKeys = new Set(members.map(memberKey));

  const medicationById = new Map(medications.map((medication) => [medication.id, medication]));

  return <Box>
    <Typography sx={{ color: '#85928D', fontSize: '0.78rem', fontWeight: 900, letterSpacing: '0.08em' }}>ROUNDS</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, mb: 1.25 }}>One reminder for a group of medications, confirmed in a single step.</Typography>
    {message ? <Alert severity={message.includes('could not') ? 'warning' : 'success'} sx={{ mt: 1 }}>{message}</Alert> : null}
    <Stack spacing={1}>
      {rounds.map((round) => <Box id={`medication-round-${round.id}`} key={round.id} sx={{ border: '1px solid #DCE5E1', borderRadius: 2.5, overflow: 'hidden', bgcolor: 'background.paper' }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 1.5, py: 1.25 }}>
          <Box sx={{ width: 54, flexShrink: 0, textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 900, lineHeight: 1 }}>{formatClockTime(round.reminderTime)}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>{clockPeriod(round.reminderTime)}</Typography>
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 850 }}>{round.name}</Typography>
            <Typography variant="body2" color="text.secondary">{round.active === false ? 'Paused' : `${round.members?.length || 0} medications`}</Typography>
          </Box>
          {canManage ? <Switch checked={round.active !== false} disabled={busy} onChange={() => save({ ...round, active: round.active === false }, round.id)} inputProps={{ 'aria-label': `${round.active === false ? 'Turn on' : 'Turn off'} ${round.name}` }} /> : null}
        </Stack>
        <Box sx={{ px: 1.5, py: 1.1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {(round.members || []).map((item) => {
              const medication = medicationById.get(item.medicationId);
              const dose = [medication?.dose, medication?.unit].filter(Boolean).join(' ');
              return <Box key={memberKey(item)} sx={{ px: 1, py: 0.4, borderRadius: 1.3, bgcolor: '#F1F5F3', color: '#455650', fontSize: '0.8rem' }}>{medication?.name || 'Medication'}{dose ? ` ${dose}` : ''}</Box>;
            })}
          </Stack>
          {round.members?.some((item) => !availableMemberKeys.has(memberKey(item))) ? <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.8, fontWeight: 800 }}>A medication schedule changed. Please review this round.</Typography> : null}
          {canManage ? <Button size="small" onClick={() => { setEditingRoundId(round.id); setDraft({ ...round, memberKeys: (round.members || []).map(memberKey), weekdays: round.weekdays?.length ? round.weekdays : weekdayKeys }); setOpen(true); }} sx={{ mt: 0.5, px: 0 }}>Edit round</Button> : null}
        </Box>
      </Box>)}
    </Stack>
    {canManage && suggestions.length && !rounds.length ? <Button fullWidth variant="contained" disabled={busy} onClick={saveSuggestions} sx={{ mt: 1.5 }}>Set up suggested rounds</Button> : null}
    {canManage ? <Button fullWidth variant="text" onClick={() => { setEditingRoundId(''); setOpen((value) => !value); }} sx={{ mt: 0.5 }}>Create custom round</Button> : null}
    <Collapse in={open}>
      <Stack spacing={1.2} sx={{ mt: 1 }}>
        <TextField size="small" label="Round name" value={draft.name} onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))} placeholder="Evening medications" />
        <TextField size="small" label="Reminder time" type="time" value={draft.reminderTime} onChange={(event) => setDraft((current) => ({ ...current, reminderTime: event.target.value }))} InputLabelProps={{ shrink: true }} />
        <Typography variant="body2" sx={{ fontWeight: 800 }}>Included doses</Typography>
        {members.map((member) => <FormControlLabel key={memberKey(member)} control={<Checkbox checked={draft.memberKeys.includes(memberKey(member))} onChange={() => setDraft((current) => ({ ...current, memberKeys: current.memberKeys.includes(memberKey(member)) ? current.memberKeys.filter((key) => key !== memberKey(member)) : [...current.memberKeys, memberKey(member)] }))} />} label={`${member.medicationName} · ${member.scheduledTime}`} />)}
        {selectionOutsideRoundWindow ? <Alert severity="info">Choose doses scheduled within 30 minutes of one another.</Alert> : null}
        <Typography variant="body2" sx={{ fontWeight: 800 }}>Days</Typography>
        <Stack direction="row" flexWrap="wrap">{weekdayKeys.map((day) => <FormControlLabel key={day} control={<Checkbox size="small" checked={draft.weekdays.includes(day)} onChange={() => setDraft((current) => ({ ...current, weekdays: current.weekdays.includes(day) ? current.weekdays.filter((value) => value !== day) : [...current.weekdays, day] }))} />} label={day.slice(0, 1).toUpperCase()} />)}</Stack>
        <Stack direction="row" spacing={1}><TextField fullWidth size="small" label="Start date" type="date" value={draft.startDate} onChange={(event) => setDraft((current) => ({ ...current, startDate: event.target.value }))} InputLabelProps={{ shrink: true }} /><TextField fullWidth size="small" label="End date" type="date" value={draft.endDate} onChange={(event) => setDraft((current) => ({ ...current, endDate: event.target.value }))} InputLabelProps={{ shrink: true }} /></Stack>
        <Button variant="contained" disabled={busy || !draft.name.trim() || !selectedMembers.length || selectionOutsideRoundWindow} onClick={() => save({ ...draft, members: selectedMembers.map(({ medicationId, scheduledTime }) => ({ medicationId, scheduledTime })) }, editingRoundId)}>Save medication round</Button>
      </Stack>
    </Collapse>
  </Box>;
};

export { buildScheduledMembers, buildSuggestedRounds };
export default MedicationRoundSetup;
